import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  uploadFileToFirebase,
  sanitizeFileName,
  compressVideo,
} from '../utils/upload';

const escapeHtml = (str) =>
  str.replace(/[&<>"']/g, (match) => {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return map[match];
  });

const formatDurationToISO8601 = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `PT${mins > 0 ? `${mins}M` : ''}${secs}S`;
};

const validateInputs = (file, title, tags) => {
  const errors = [];
  if (!file) errors.push('❌ 動画ファイルを選んでください');
  else {
    if (!file.type.startsWith('video/')) errors.push('❌ 動画ファイル形式が無効です');
    if (file.size > 200 * 1024 * 1024) errors.push('❌ 動画サイズは200MB以内にしてください');
  }
  const trimmedTitle = title.trim();
  if (trimmedTitle.length < 3) errors.push('❌ タイトルは3文字以上で入力してください');
  if (trimmedTitle.length > 100) errors.push('❌ タイトルは100文字以内にしてください');
  if (/[^\wぁ-んァ-ヶ一-龠ー\s]/.test(trimmedTitle)) errors.push('❌ タイトルに使用できない文字が含まれています');
  const tagArray = tags.split(',').map((t) => t.trim()).filter(Boolean);
  if (tagArray.length > 10) errors.push('❌ タグは最大10個までです');
  if (tagArray.some((tag) => tag.length > 15)) errors.push('❌ 各タグは15文字以内で入力してください');
  if (new Set(tagArray).size !== tagArray.length) errors.push('❌ タグが重複しています');
  return errors;
};

const UploadVideoForm = ({ onUploadComplete }) => {
  const [videoFile, setVideoFile] = useState(null);
  const [videoTitle, setVideoTitle] = useState(() => localStorage.getItem('videoTitle') || '');
  const [videoDescription, setVideoDescription] = useState('');
  const [videoTags, setVideoTags] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [progressSteps, setProgressSteps] = useState([]);
  const [progress, setProgress] = useState(0);
  const [errorDialog, setErrorDialog] = useState('');

  useEffect(() => () => previewUrl && URL.revokeObjectURL(previewUrl), [previewUrl]);

  const resetForm = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setVideoFile(null);
    setVideoTitle('');
    setVideoDescription('');
    setVideoTags('');
    setPreviewUrl('');
    setThumbnailUrl('');
    setProgressSteps([]);
    setProgress(0);
    setErrorDialog('');
  };

  const isValid = () => validateInputs(videoFile, videoTitle, videoTags).length === 0;

  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const steps = [];
      setProgressSteps(['⏳ 圧縮中...']);
      setProgress(0);

      const baseName = sanitizeFileName(videoTitle.trim() || file.name.split('.')[0]);

      let compressed;
      try {
        compressed = await compressVideo(file);
      } catch (err) {
        console.error('❌ 圧縮エラー:', err);
        throw new Error('動画の圧縮中にエラーが発生しました');
      }

      // duration取得
      let durationSec = 0;
      try {
        const videoBlobUrl = URL.createObjectURL(compressed);
        durationSec = await new Promise((resolve, reject) => {
          const tempVideo = document.createElement('video');
          tempVideo.preload = 'metadata';
          tempVideo.src = videoBlobUrl;
          tempVideo.onloadedmetadata = () => {
            URL.revokeObjectURL(videoBlobUrl);
            resolve(tempVideo.duration);
          };
          tempVideo.onerror = () => {
            URL.revokeObjectURL(videoBlobUrl);
            reject(new Error('動画の長さ取得に失敗しました'));
          };
        });
      } catch (err) {
        console.warn('duration取得失敗:', err);
      }

      const isoDuration = formatDurationToISO8601(durationSec);

      steps.push('✅ 圧縮完了');
      setProgressSteps([...steps, '⏳ 動画アップロード中...']);

      let videoResult;
      try {
        videoResult = await uploadFileToFirebase(
          compressed,
          'videos',
          baseName,
          setProgress,
          {
            firestoreCollection: 'videos',
            title: videoTitle,
            metadata: {
              title: videoTitle,
              description: escapeHtml(videoDescription.trim().slice(0, 300)) || '説明なし',
              tags: videoTags.split(',').map((t) => t.trim()).filter(Boolean).slice(0, 10),
              duration: isoDuration,
            },
            initViewCounter: true,
          }
        );
      } catch (err) {
        console.error('❌ アップロードエラー:', err);
        throw new Error('Firebaseへのアップロードに失敗しました');
      }

      steps.push('✅ 動画アップロード完了');
      setProgressSteps([...steps, '🎉 アップロードが完了しました！']);
      setProgress(100);
      return { ...videoResult, title: videoTitle };
    },
    onSuccess: (result) => {
      console.log('🎯 完了:', result);
      localStorage.setItem('videoTitle', videoTitle);
      setThumbnailUrl(result.thumbUrl);
      onUploadComplete?.(result);
      resetForm();
    },
    onError: (err) => {
      console.error('❌ アップロードエラー:', err);
      setProgressSteps([]);
      setErrorDialog(err.message || 'アップロード中にエラーが発生しました');
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    },
  });

  const handleUpload = () => {
    const errors = validateInputs(videoFile, videoTitle, videoTags);
    if (errors.length > 0) {
      setProgressSteps(errors);
      return;
    }
    setProgressSteps(['⏳ アップロード開始します...']);
    setProgress(0);
    setErrorDialog('');
    uploadMutation.mutate(videoFile);
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.heading}>🎬 動画アップロード</h3>
      {errorDialog && (
        <div style={styles.errorDialog}>
          <p>⚠️ {errorDialog}</p>
          <button onClick={() => setErrorDialog('')} style={styles.buttonRetry}>閉じる</button>
        </div>
      )}
      <input
        type="text"
        value={videoTitle}
        onChange={(e) => setVideoTitle(e.target.value)}
        placeholder="動画タイトル"
        disabled={uploadMutation.isLoading}
        style={styles.input}
      />
      <textarea
        value={videoDescription}
        onChange={(e) => setVideoDescription(e.target.value)}
        placeholder="動画説明"
        disabled={uploadMutation.isLoading}
        style={styles.textarea}
      />
      <input
        type="text"
        value={videoTags}
        onChange={(e) => setVideoTags(e.target.value)}
        placeholder="タグ (カンマ区切り)"
        disabled={uploadMutation.isLoading}
        style={styles.input}
      />

      <div style={styles.row}>
        <label htmlFor="videoFile" style={styles.fileLabel}>動画を選択</label>
        <input
          id="videoFile"
          type="file"
          accept="video/*"
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) {
              if (previewUrl) URL.revokeObjectURL(previewUrl);
              setVideoFile(file);
              setPreviewUrl(URL.createObjectURL(file));
            }
          }}
          style={{ display: 'none' }}
        />

        <button
          onClick={handleUpload}
          disabled={!isValid() || uploadMutation.isLoading}
          style={!isValid() || uploadMutation.isLoading ? styles.buttonLoading : styles.button}
        >
          {uploadMutation.isLoading ? 'アップロード中...' : 'アップロード'}
        </button>

        <button onClick={resetForm} style={styles.buttonReset}>リセット</button>
      </div>

      {progress > 0 && (
        <div style={styles.progressContainer}>
          <div style={{ ...styles.progressBar, width: `${progress}%` }} />
        </div>
      )}

      <ul style={styles.stepList}>
        {progressSteps.map((step, idx) => (
          <li key={idx} style={{ color: step.startsWith('❌') ? 'red' : step.startsWith('⚠️') ? 'orange' : 'green' }}>
            {step}
          </li>
        ))}
      </ul>

      {previewUrl && <video src={previewUrl} controls style={styles.preview} />}
      {thumbnailUrl && <img src={thumbnailUrl} alt="サムネイル" style={styles.thumbnail} />}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    marginBottom: '24px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    fontFamily: "'Helvetica Neue', 'Arial', sans-serif",
  },
  heading: {
    fontSize: '22px',
    marginBottom: '20px',
    color: '#e91e63',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  input: {
    width: '100%',
    padding: '12px',
    marginBottom: '14px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    marginBottom: '14px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '16px',
  },
  fileLabel: {
    backgroundColor: '#e91e63',
    color: '#fff',
    padding: '12px 18px',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    textAlign: 'center',
  },
  button: {
    backgroundColor: '#e91e63',
    color: '#fff',
    padding: '12px 18px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    textAlign: 'center',
  },
  buttonLoading: {
    backgroundColor: '#bbb',
    cursor: 'not-allowed',
    padding: '12px 18px',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 'bold',
  },
  buttonRetry: {
    backgroundColor: '#f44336',
    color: '#fff',
    padding: '12px 18px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    textAlign: 'center',
  },
  buttonReset: {
    backgroundColor: '#bbb',
    color: '#fff',
    padding: '12px 18px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    textAlign: 'center',
  },
  progressContainer: {
    width: '100%',
    height: '10px',
    backgroundColor: '#ffe0e6',
    borderRadius: '5px',
    overflow: 'hidden',
    marginTop: '16px',
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#e91e63',
    transition: 'width 0.4s ease',
  },
  stepList: {
    listStyleType: 'none',
    padding: '0',
    marginTop: '16px',
    fontSize: '15px',
    lineHeight: '1.6',
    color: '#555',
    textAlign: 'left',
  },
  preview: {
    marginTop: '16px',
    maxWidth: '100%',
    borderRadius: '8px',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
  },
  thumbnail: {
    marginTop: '16px',
    width: '160px',
    borderRadius: '8px',
    objectFit: 'cover',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
  },
  errorDialog: {
    backgroundColor: '#fdecea',
    color: '#d32f2f',
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #f44336',
    textAlign: 'left',
  },
};

export default UploadVideoForm;
